import { faPencilAlt } from "@fortawesome/pro-solid-svg-icons/faPencilAlt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CardContent, Grid, Typography } from "@mui/material";
import { StyledComponentProps, withStyles } from "@mui/styles";
import { FeedClassKey, FeedStyleClasses, GlobalClassKeys, TimelineClassKey, TimelineStyleClasses } from "assets/jss/common-styles";
import { AwardSnackbarMessage } from "components/AwardSnackbarMessage";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import CardColor from "components/MuiExtras/Card/CardColor";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiLink from "components/MuiExtras/MuiLink";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import { FeedType, PublicationType, SystemRoles } from "models/ApiEnums";
import { SavePublicationResult } from "models/ApiModels";
import * as React from "react";
import { Trans, WithTranslation, withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import * as FeedActions from 'store/feed/actions';
import { FeedState } from "store/feed/reducer";
import { ApplicationState } from "store/rootReducer";
import * as SideBarActions from 'store/sidebar/actions';
import CardManager from "views/Timeline/Components/CardManager";
import { publicationFactory } from "views/Timeline/Components/FeedHelpers";
import { ITimelineDispatchProps, TimelineWindowMessageListener } from "views/Timeline/TimelineCommons";
import Session from "services/Session";


/////////////////////////////////////////////////////////////////////////////////////////////////

interface IStateProps {
    feed: FeedState;
}

interface IState {
    orderByMenuAnchorEl: any;
    dialog: any;
}

interface IProps {
    timestamp?: Date;
}

interface IDispatchProps extends ITimelineDispatchProps {
    onGetFeed: (startIndex: number, endIndex: number) => Promise<any>;
}

type Props = IProps & IStateProps & IDispatchProps & StyledComponentProps<TimelineClassKey | FeedClassKey> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////

class TimelineStartupComponent extends React.PureComponent<Props, IState> {

    private wmm: TimelineWindowMessageListener;

    constructor(props: Props) {
        super(props);
        this.state = {
            orderByMenuAnchorEl: null,
            dialog: null
        };
    }

    ///
    public feedReset() {
        this.props.onFeedReset();
    }

    ///
    private handleClose = (result: SavePublicationResult) => {
        if (result) {
            if (result.award) {
                this.props.snackbar.showMessage(<AwardSnackbarMessage award={result.award} />, "trophy");
            }
            this.props.onAddPublication(result.publicationId);
        }

        this.setState({ dialog: null });
    };

    ///
    private handleAction(action: string) {
        switch (action) {
            case "post": {
                this.setState({ dialog: publicationFactory(null, PublicationType.Post, this.handleClose) });
                break;
            }
            case "question": {
                this.setState({ dialog: publicationFactory(null, PublicationType.Question, this.handleClose) });
                break;
            }
        }
    }

    ///
    private renderTopMessage() {
        return (
            <CardColor color="notice">
                <CardContent>
                    <Grid container spacing={1} wrap="nowrap" >
                        <Grid item className={GlobalClassKeys.alignSelfCenter}>
                            <FontAwesomeIcon icon={faPencilAlt} fixedWidth size={"2x"} color={"#BF9000"} />
                        </Grid>
                        <Grid item className={GlobalClassKeys.grow}>
                            <Typography variant="body1">
                                <Trans
                                    i18nKey="Views.Timeline.TimelineStartup.TopMessageL1"
                                    components={
                                        [
                                            <MuiLink onClick={() => { this.handleAction("question"); }}>{this.props.t("Views.Timeline.TimelineStartup.QuestionText")}</MuiLink>,
                                            <MuiLink onClick={() => { this.handleAction("post"); }}>{this.props.t("Views.Timeline.TimelineStartup.InformationText")}</MuiLink>
                                        ]
                                    }
                                />
                            </Typography>
                            <Typography variant="body2">
                                <MuiMuted variant="default">{this.props.t("Views.Timeline.TimelineStartup.TopMessageL2")}</MuiMuted>
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </CardColor>
        );
    }

    ///
    public componentDidMount() {
        this.feedReset();
        this.wmm = new TimelineWindowMessageListener(this.props.executeFeedMessage);
    }

    ///
    public componentWillUnmount() {
        this.feedReset();
        this.wmm.dettach();
    }

    ///
    public componentDidUpdate(prevProps: Props) {
        if (prevProps.timestamp != this.props.timestamp && !this.props.feed.loading)
            this.feedReset();
    }

    ///
    public render() {
        const { classes, feed, feed: { lastError, loading }, snackbar } = this.props;
        const hasPublisher = Session.hasRole(SystemRoles.Publisher);

        return (
            <div className={classes.timelineContainer}>

                {this.state.dialog}

                  <MuiContainer margin="dense">
                    {hasPublisher && this.renderTopMessage()}
                  </MuiContainer>

                <CardManager
                    classes={classes}
                    feedType={FeedType.Start}
                    feed={feed}
                    snackbar={snackbar}
                    onExpandPublication={this.props.onExpandPublication}
                    onRemovePublication={this.props.onRemovePublication}
                    onReloadPublication={this.props.onReloadPublication}
                    onChangePublicationVisibility={this.props.onChangePublicationVisibility}
                    onUpdatePublication={this.props.onUpdateFeedData}
                    onIgnoreActionRequest={this.props.onIgnoreActionRequest}
                    onSidebarResetRequest={this.props.onSidebarResetRequest}
                    onRemoveFromFeed={this.props.onRemoveFromFeed}
                    onGetFeed={(startIndex: number, endIndex: number) => { return this.props.onGetFeed(startIndex, endIndex); }}
                    onFeedReset={() => this.props.onFeedReset()}
                />

                {
                    lastError != null
                        ? <RequestErrorReport error={lastError} />
                        : this.props.feed.data.length == 0 && !loading
                            ? <MuiMessageBox variant="info" message={this.props.t("Common.Feed.Filter.PublicationFilteredResultText_zero")} />
                            : null
                }

            </div>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

function mapStateToProps(state: ApplicationState): IStateProps {
    return {
        feed: state.feed
    };
}

function mapDispatchToProps(dispatch: ThunkDispatch<ApplicationState, void, Action<any>>, ownProps: IProps): IDispatchProps {
    const action = new FeedActions.MainFeedAction();

    return {
        onAddPublication: (publicationId) => dispatch(action.addPublication(publicationId)),
        onChangePublicationVisibility: (uid, publicationId, visibility) => dispatch(action.changePublicationVisibility(uid, publicationId, visibility)),
        onExpandPublication: (uid, publicationId) => dispatch(action.getPublicationFullContent(uid, publicationId)),
        onReloadPublication: (uid, publicationId) => dispatch(action.reloadPublication(uid, publicationId)),
        onRemovePublication: (uid, publicationId, publicationType) => dispatch(action.removePublication(uid, publicationId, publicationType)),
        onUpdateFeedData: (newItemDto) => dispatch(action.updateFeedData(newItemDto)),
        onRemoveFromFeed: (uid, publicationId) => dispatch(action.removeFeedData(uid, publicationId)),
        onFeedReset: () => dispatch(action.feedReset()),

        onGetFeed: (startIndex, endIndex) => dispatch(action.getFeedMain(startIndex, endIndex)),

        onSidebarResetRequest: () => dispatch(SideBarActions.reset()),

        executeFeedMessage: (messageType) => action.executeFeedMessage(messageType, dispatch)
    };
}

const withAllStyles = withStyles(
    (theme) => ({
        ...TimelineStyleClasses(theme),
        ...FeedStyleClasses(theme),
    }),
    { withTheme: true },
);

const Timeline = connect<IStateProps, IDispatchProps>(mapStateToProps, mapDispatchToProps)(withSnackbar(withTranslation()(withAllStyles(TimelineStartupComponent))));

///////////////////////////////////////////////////////////////////////////

const TimelineStartup: React.FC<Props> = () => {
    return (
        <Timeline
            timestamp={new Date()}
        />
    );
};

export default TimelineStartup;